import React, { useMemo } from "react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const parseMonth = (val) => {
  if (!val) return null;
  const parts = String(val).split("-");
  if (parts.length < 2) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (isNaN(year) || isNaN(month)) return null;
  return { year, month };
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const formatNow = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const MadEventPrint = ({
  medications,
  selectedMonth,
  fullName,
  birthDate,
  NhsNumber,
  areaName,
  clientDetails,
}) => {
  const days = useMemo(() => {
    const parsed = parseMonth(selectedMonth);
    if (!parsed) return [];
    const count = daysInMonth(parsed.year, parsed.month);
    return Array.from({ length: count }, (_, i) => i + 1);
  }, [selectedMonth]);

  const monthName = useMemo(() => {
    const parsed = parseMonth(selectedMonth);
    if (!parsed) return "";
    return MONTH_NAMES[parsed.month - 1];
  }, [selectedMonth]);

  const makeDateKey = (day) =>
    `${selectedMonth}-${String(day).padStart(2, "0")}`;

  const getMark = (rec) => {
    if (!rec) return "";

    if (rec.reason && rec.reason.trim() !== "") {
      return rec.reason.trim().charAt(0).toUpperCase();
    }

    if (rec.status === "Given") return "✓";
    if (rec.status === "Not Given") return "✗";
    if (rec.status === "Pending") return "·";

    return "";
  };

  const reasonsLegend = [
    { code: "R", text: "Refused" },
    { code: "N", text: "Nausea" },
    { code: "H", text: "Hospitalised" },
    { code: "S", text: "Social leave" },
    { code: "U", text: "Unavailable dose" },
    { code: "C", text: "Cancelled by client" },
    { code: "A", text: "Absent client" },
    { code: "T", text: "Task already done" },
    { code: "I", text: "Independent intake" },
    { code: "F", text: "Family administered" },
    { code: "O", text: "Optional / Other" },
    { code: "G", text: "Given by partner carer" },
    { code: "P", text: "Please contact carer for info" },
  ];

  return (
    <div style={{ fontFamily: "Arial, sans-serif", margin: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ color: "#666", fontSize: "0.9rem" }}>
          {formatNow()}
        </div>
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          {fullName ? `${fullName}'s Medication Chart` : "Medication Chart"}
        </div>
        <div style={{ textAlign: "right", fontWeight: "bold" }}>
          Official - Sensitive
        </div>
      </div>

      <div style={{ display: "flex", marginBottom: "25px" }}>
        {clientDetails?.profile_photo ? (
          <img
            src={clientDetails.profile_photo}
            alt={fullName || "Client"}
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              objectFit: "cover",
              marginRight: "15px",
            }}
          />
        ) : (
          <div
            style={{
              width: "70px",
              height: "70px",
              backgroundColor: "#f0f0f0",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              textAlign: "center",
              marginRight: "15px",
            }}
          >
            <span>
              NO
              <br />
              IMAGE
              <br />
              AVAILABLE
            </span>
          </div>
        )}
        <div>
          <div
            style={{
              fontWeight: "bold",
              fontSize: "1.2rem",
              marginBottom: "5px",
            }}
          >
            {fullName || "N/A"}
          </div>
          <div>Date of Birth : {birthDate || "N/A"}</div>
          <div>Location : {areaName || "N/A"}</div>
          <div>Address : {clientDetails?.address_1 || "N/A"}</div>
          <div>NHS Number : {NhsNumber || "N/A"}</div>
          <div>Month: {monthName}</div>
        </div>
      </div>

      <h3
        style={{
          color: "black",
          borderBottom: "2px solid black",
          paddingBottom: "5px",
          marginBottom: "10px",
        }}
      >
        Medical Event
      </h3>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "10px",
          fontSize: "0.85rem",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid #000",
                padding: "4px",
                textAlign: "left",
                background: "#f0f0f0",
              }}
            >
              Medication / Visit
            </th>
            {days.map((d) => (
              <th
                key={d}
                style={{
                  border: "1px solid #000",
                  padding: "4px",
                  width: "24px",
                  textAlign: "center",
                  background: "#f9f9f9",
                }}
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {medications.map((med, mi) => (
            <React.Fragment key={mi}>
              <tr>
                <td
                  colSpan={days.length + 1}
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    background: "#e8f4ff",
                  }}
                >
                  <strong>{med.name || med.title}</strong> | Type: {med.type} |
                  Frequency: {med.frequency} | Dose: {med.dose || "–"} | Start:{" "}
                  {med.startDate}
                </td>
              </tr>

              {med.visits.map((visit, vi) => (
                <tr key={vi}>
                  <td
                    style={{
                      border: "1px solid #000",
                      padding: "4px",
                      fontStyle: "italic",
                    }}
                  >
                    {visit.label}
                  </td>
                  {days.map((day) => {
                    const key = makeDateKey(day);
                    const rec = visit.data.find((d) => d.date === key);

                    return (
                      <td
                        key={day}
                        style={{
                          border: "1px solid #000",
                          padding: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                          color:
                            rec?.status === "Not Given" ||
                            rec?.status === "Missed"
                              ? "red"
                              : "black",
                        }}
                      >
                        {getMark(rec)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div
        style={{
          marginTop: "20px",
          fontSize: "0.75rem",
          border: "1px solid #ccc",
          padding: "10px",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "5px",
            textDecoration: "underline",
          }}
        >
          Legend / Codes:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
          <div>
            <strong>✓</strong> : Given
          </div>
          <div>
            <strong>✗</strong> : Not Given (Missed)
          </div>
          {reasonsLegend.map((item) => (
            <div key={item.code}>
              <strong>{item.code}</strong> : {item.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MadEventPrint;